/*
  Cart drawer helpers.

  1. Apps that change the cart themselves (Visually upsells, the Stylux personalization
     modal) call /cart/add|change|update|clear without asking for the drawer sections,
     so Dawn never re-renders the drawer. Watch those requests and re-render it.
     Dawn's own requests always send `sections`, which is how they are told apart.

  2. While <cart-drawer data-suppress-rebuy> is set (theme setting), tear down the Rebuy
     Smart Cart so this drawer is the one that opens. Only needed until Rebuy's Smart
     Cart is switched off in the Rebuy admin.
*/
(() => {
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  const sendsSections = (body) => {
    if (!body) return false;
    if (typeof body === 'string') return body.includes('sections');
    if (body instanceof FormData || body instanceof URLSearchParams) return body.has('sections');
    return false;
  };

  let refreshTimer;
  const scheduleRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshDrawer, 150);
  };

  async function refreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    try {
      const response = await originalFetch(`${window.routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
      const sections = await response.json();
      const parser = new DOMParser();

      const nextDrawer = parser.parseFromString(sections['cart-drawer'], 'text/html').querySelector('cart-drawer');
      const nextInner = nextDrawer && nextDrawer.querySelector('.drawer__inner');
      const inner = drawer.querySelector('.drawer__inner');
      if (nextInner && inner) {
        inner.innerHTML = nextInner.innerHTML;
        drawer.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
      }

      const bubble = document.getElementById('cart-icon-bubble');
      const nextBubble = parser
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');
      if (bubble && nextBubble) bubble.innerHTML = nextBubble.innerHTML;
    } catch (error) {
      console.error(error);
    }
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    const request = originalFetch(input, init);
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (CART_MUTATION.test(url) && !sendsSections(init && init.body)) {
      request.then((response) => response.ok && scheduleRefresh()).catch(() => {});
    }
    return request;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.nlCartUrl = String(url);
    return originalOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (CART_MUTATION.test(this.nlCartUrl || '') && !sendsSections(body)) {
      this.addEventListener('load', () => this.status < 400 && scheduleRefresh());
    }
    return originalSend.apply(this, arguments);
  };

  // Send every link to the cart page to the drawer, not just #cart-icon-bubble. Visually opens
  // the cart after an upsell add by clicking the first a[href="/cart"] on the page, which is the
  // mobile menu's cart link; followed, it loads /cart and bounces back with ?viewcart=true.
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const link = event.target.closest && event.target.closest('a[href]');
    const drawer = document.querySelector('cart-drawer');
    if (!link || !drawer || link.pathname !== window.routes.cart_url) return;
    event.preventDefault();
    drawer.open(link);
  });

  if (document.querySelector('cart-drawer[data-suppress-rebuy]')) {
    document.documentElement.classList.add('nl-suppress-rebuy');

    // Rebuy binds the header cart icon before its Smart Cart has loaded, so take the
    // click in the capture phase, ahead of it.
    window.addEventListener(
      'click',
      (event) => {
        const icon = event.target.closest && event.target.closest('#cart-icon-bubble');
        if (!icon) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        document.querySelector('cart-drawer').open(icon);
      },
      true
    );

    // Whenever Rebuy renders its cart (e.g. after an add to cart), tear it down again.
    new MutationObserver(() => {
      const smartCart = window.Rebuy && window.Rebuy.SmartCart;
      if (document.getElementById('rebuy-cart') && smartCart && typeof smartCart.destroy === 'function') {
        smartCart.destroy();
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
